/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { getMyTasks, getTask } from './api/tasks'
import { getFileDetail } from './api/files'
import { useModal } from '../contexts/ModalContext'
import { TaskStatus } from '../constants/task'
import { WorkflowStepType, WorkflowType } from '../constants/workflow'
import { FsiType } from '../constants/fsi'

interface NextTaskData {
  id: number
  workflowStepType: string
  workflowType: string
  files: Array<{
    draftFsi?: FileSystemItem
    targetFsi?: FileSystemItem
    sourceFsi?: FileSystemItem
  }>
}

interface FileSystemItem {
  id: number
  type: string
  versionId: number
  minorVersionId: number
}

/**
 * Next Task Service
 * Handles checking for and navigating to the next available task
 */
export class NextTaskService {
  private showModal: (component: React.ReactNode) => string
  private navigate: (path: string, options?: any) => void

  constructor(showModal: (component: React.ReactNode) => string, navigate: (path: string, options?: any) => void) {
    this.showModal = showModal
    this.navigate = navigate
  }

  /**
   * Check if there's a next task available and show modal
   */
  async checkNextTask(): Promise<void> {
    try {
      const response = await getMyTasks()

      if (response && response.elements && response.elements.length === 1) {
        const task = response.elements[0]
        // Show next task modal - this would need to be a React component
        // For now, log the task
        console.log('Next task available:', task)
        // TODO: Implement NextTaskModal React component
        // this.showModal(<NextTaskModal task={task} onGoToTask={this.goToNextTask} />)
      }
    } catch (error) {
      console.error('Error checking next task:', error)
    }
  }

  /**
   * Navigate to the next task
   * @param nextTask - The next task to navigate to
   * @param secondRun - Whether this is a retry (for training tasks)
   */
  async goToNextTask(nextTask: NextTaskData, secondRun = false): Promise<void> {
    const file = this.getFileFromTask(nextTask)

    if (file && file.type !== FsiType.PLACEHOLDER) {
      console.log('Navigating to next task:', nextTask)

      // For training tasks, verify file exists first
      if (nextTask.workflowStepType === WorkflowStepType.TRAINING && !secondRun) {
        try {
          await getFileDetail(file.id)
          this.navigateToShowFile(file, nextTask)
        } catch (error: any) {
          // If file not found (404), re-fetch task to get updated file info
          if (error?.status === 404) {
            const updatedTask = await getTask(String(nextTask.id))
            await this.goToNextTask(updatedTask as any, true)
          }
        }
      } else {
        this.navigateToShowFile(file, nextTask)
      }
    } else {
      // If no valid file, open task modal directly
      console.log('Opening task approve modal for:', nextTask.id)
      // TODO: Implement TaskApproveModal React component
      // this.showModal(<TaskApproveModal taskId={nextTask.id} task={nextTask} />)
    }
  }

  /**
   * Get the first available file from task
   */
  private getFileFromTask(nextTask: NextTaskData): FileSystemItem | null {
    if (!nextTask.files || nextTask.files.length === 0) {
      return null
    }

    const fileWrapper = nextTask.files[0]
    return fileWrapper.draftFsi || fileWrapper.targetFsi || fileWrapper.sourceFsi || null
  }

  /**
   * Navigate to the file preview page
   */
  private navigateToShowFile(file: FileSystemItem, nextTask: NextTaskData): void {
    // Determine route based on workflow type
    const isQCReview = nextTask.workflowType === WorkflowType.QC_REVIEW
    const route = isQCReview ? '/file-qc' : '/file'

    // Navigate with query params
    this.navigate(`${route}/${file.id}`, {
      state: {
        action: 'taskComplete',
        taskId: nextTask.id,
        versionId: file.versionId,
        minorVersionId: file.minorVersionId
      }
    })
  }
}

/**
 * React Hook to use nextTask service
 * This should be used within components that have access to useModal and useNavigate
 */
export function useNextTask() {
  const { showModal } = useModal()
  const navigate = (path: string, options?: any) => {
    // This would use React Router's useNavigate in the actual component
    // For now, just a placeholder
    console.log('Navigate to:', path, options)
  }

  return new NextTaskService(showModal, navigate)
}

/**
 * Standalone function for checking next task (without hooks)
 * Use this when you can't use hooks (e.g., in callbacks)
 */
export async function checkNextTask(): Promise<void> {
  try {
    const response = await getMyTasks()

    if (response && response.elements && response.elements.length === 1) {
      const task = response.elements[0]
      console.log('Next task available:', task)
      // TODO: Show modal or navigate
    }
  } catch (error) {
    console.error('Error checking next task:', error)
  }
}
